// ---------------------------------------------------------------------------
// Discovery Service — wrapper do callable resolveBookIdentity
// Usado para popular book_meta em background ao adicionar livros.
// ---------------------------------------------------------------------------

import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";

import { runBookIdentityResolve } from "./bookIdentityResolveGate";

export interface ResolveBookIdentityInput {
  isbn13?: string;
  isbn10?: string;
  appleId?: string;
  googleId?: string;
  title?: string;
  author?: string;
}

export interface BookMetaSummary {
  canonicalId: string;
  coverUrl: string;
  totalPages: number | null;
  language: string;
  qualityScore: number;
}

export interface ResolveBookIdentityResult {
  canonicalId: string;
  meta: BookMetaSummary;
}

const functionsRegion = import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION as string | undefined;

function getFunctionsInstance() {
  const app = getApp();
  return functionsRegion?.trim()
    ? getFunctions(app, functionsRegion.trim())
    : getFunctions(app);
}

// Extrai identificadores do bookId conforme o prefixo da fonte:
//  - apple-XXX        → appleId
//  - brasilapi-XXX    → ISBN (BrasilAPI sempre retorna ID com ISBN no rabo)
//  - openlibrary-XXX  → ISBN se tiver 10/13 dígitos, senão só title+author
//  - default          → googleId (volumeId do Google Books, único sem prefixo)
export function parseBookIdToInput(
  bookId: string,
  title?: string,
  author?: string,
): ResolveBookIdentityInput | null {
  // Ficha comunitária (`manual:`): NÃO resolve. Mandar `{title, author}` ao
  // servidor criaria um `slug:` duplicado da mesma ficha, e mandar o id como
  // `googleId` (o default abaixo) seria lixo. `null` = "não há o que resolver".
  if (bookId.startsWith("manual:")) return null;
  // Canônicos com dois-pontos (ids de book_meta): usados quando a BookDetail
  // acorda o enrichment pra um livro JÁ catalogado (ex.: capa-fallback,
  // BUG-20). Sem estes branches, `isbn:X` caía no default e virava
  // googleId lixo.
  if (bookId.startsWith("isbn:")) {
    const tail = bookId.slice(5);
    if (/^\d{13}$/.test(tail)) return {isbn13: tail, title, author};
    if (/^\d{10}$/.test(tail)) return {isbn10: tail, title, author};
    return {title, author};
  }
  if (bookId.startsWith("google:")) {
    return {googleId: bookId.slice(7), title, author};
  }
  if (bookId.startsWith("apple:")) {
    return {appleId: bookId.slice(6), title, author};
  }
  if (bookId.startsWith("slug:")) {
    return {title, author};
  }
  if (bookId.startsWith("apple-")) {
    return {appleId: bookId.replace("apple-", ""), title, author};
  }
  // Legacy hyphen `google-XXX` (user_books de import/pré-refactor). Sem este
  // branch o id caía CRU no fallback googleId → o backend tentava o volume
  // "google-XXX" na API (503) e criava alias sujo (BUG-11).
  if (bookId.startsWith("google-")) {
    return {googleId: bookId.replace("google-", ""), title, author};
  }
  if (bookId.startsWith("brasilapi-")) {
    const isbn = bookId.replace("brasilapi-", "");
    if (/^\d{13}$/.test(isbn)) return {isbn13: isbn, title, author};
    if (/^\d{10}$/.test(isbn)) return {isbn10: isbn, title, author};
    return {title, author};
  }
  if (bookId.startsWith("openlibrary-")) {
    const tail = bookId.replace("openlibrary-", "");
    if (/^\d{13}$/.test(tail)) return {isbn13: tail, title, author};
    if (/^\d{10}$/.test(tail)) return {isbn10: tail, title, author};
    // OL key (sem ISBN) — só title+author serve pro lookup
    return {title, author};
  }
  // Google Volume ID (sem prefixo)
  return {googleId: bookId, title, author};
}

// Fire-and-forget: resolve identidade canônica sem bloquear o fluxo de addBook.
// Erros são silenciosos — o livro já está na estante, book_meta é best-effort.
//
// ⚠️ Esta é a PORTA ÚNICA do `resolveBookIdentity` no client (9 call sites: 7
// em `useCanonicalIdRedirect`, 1 em `useBookDetailState`, 1 em `addBook`), e é
// por isso que o gate de dedup mora aqui dentro em vez de em cada chamador.
// Sem ele, abrir um livro disparava a MESMA resolução várias vezes em paralelo
// e cada cópia subia a própria instância fria — medido em prod: 3 chamadas
// idênticas simultâneas, 3 cold starts de ~4,1s, todas cache HIT.
// Ver `bookIdentityResolveGate.ts` para as decisões (TTL, falha não cacheia).
export function resolveBookIdentityBackground(input: ResolveBookIdentityInput | null): void {
  if (!input) return; // ficha comunitária — nada a resolver
  void runBookIdentityResolve(input, () => {
    const callable = httpsCallable<ResolveBookIdentityInput, ResolveBookIdentityResult>(
      getFunctionsInstance(),
      "resolveBookIdentity",
    );
    return callable(input);
  });
}

/**
 * A SEGUNDA porta do `resolveBookIdentity`, e a única que ESPERA o resultado:
 * o pote do clube (06/10, "buscar um livro pelo nome") precisa do id canônico
 * antes de chamar `addClubPotBook` — o servidor só aceita livro com ficha em
 * `book_meta`. Fora do gate de propósito: o gate não devolve o resultado, e
 * aqui a chamada nasce de um toque, não de um efeito repetido. `null` = não
 * resolveu (ficha comunitária, fonte fora do ar).
 */
export async function resolveCanonicalBookId(bookId: string, title: string, author: string): Promise<string | null> {
  if (bookId.includes(":")) return bookId;
  const input = parseBookIdToInput(bookId, title, author);
  if (!input) return null;
  try {
    const callable = httpsCallable<ResolveBookIdentityInput, ResolveBookIdentityResult>(
      getFunctionsInstance(),
      "resolveBookIdentity",
    );
    const res = await callable(input);
    return res.data?.canonicalId || null;
  } catch {
    return null;
  }
}
